import React, { useState, useEffect, useCallback } from 'react';
import { geofencesAPI, cardsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  MapPin, Plus, Loader2, Trash2, Pencil, Search, CheckCircle2, AlertTriangle, Info,
} from 'lucide-react';

const EMPTY_FORM = {
  name: '', card_id: '',
  address: '', latitude: null, longitude: null,
  addressLabel: '', addressScore: null,
  radius: 200, message: '', is_active: true,
};

const scoreColor = (score) => {
  if (score >= 0.7) return 'bg-green-100 text-green-700 border-green-200';
  if (score >= 0.4) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
};
const scoreLabel = (score) => {
  if (score >= 0.7) return `Excellente correspondance (${Math.round(score * 100)}%)`;
  if (score >= 0.4) return `Correspondance moyenne (${Math.round(score * 100)}%)`;
  return `Correspondance faible (${Math.round(score * 100)}%)`;
};

const GeofencesPage = () => {
  const [geofences, setGeofences] = useState([]);
  const [meta, setMeta] = useState({ active_count: 0, total_count: 0, plan_limit: null, apple_limit: 10, apple_limit_reached: false });
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [geoRes, cardsRes] = await Promise.all([geofencesAPI.list(), cardsAPI.list()]);
      setGeofences(geoRes.data.geofences || []);
      setMeta({
        active_count: geoRes.data.active_count || 0,
        total_count: geoRes.data.total_count || 0,
        plan_limit: geoRes.data.plan_limit,
        apple_limit: geoRes.data.apple_limit || 10,
        apple_limit_reached: geoRes.data.apple_limit_reached || false,
      });
      setCards(cardsRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Impossible de charger les zones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM, card_id: cards[0]?.id || '' });
    setDialogOpen(true);
  };

  const openEdit = (geo) => {
    setEditingId(geo.id);
    setFormData({
      name: geo.name || '',
      card_id: geo.card_id || '',
      address: geo.address || '',
      latitude: geo.latitude, longitude: geo.longitude,
      addressLabel: geo.address || '',
      addressScore: geo.address ? 1 : null,
      radius: geo.radius || 200,
      message: geo.message || '',
      is_active: geo.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleGeocode = async () => {
    const addr = (formData.address || '').trim();
    if (addr.length < 4) {
      toast.error("Saisissez une adresse d'au moins 4 caractères");
      return;
    }
    setGeocoding(true);
    try {
      const { data } = await geofencesAPI.geocode(addr);
      setFormData((prev) => ({
        ...prev,
        latitude: data.latitude,
        longitude: data.longitude,
        addressLabel: data.label,
        addressScore: data.score,
      }));
      toast.success('Adresse géocodée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Adresse introuvable');
      setFormData((prev) => ({ ...prev, latitude: null, longitude: null, addressLabel: '', addressScore: null }));
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Le nom est obligatoire'); return; }
    if (!formData.card_id) { toast.error('Choisissez une carte'); return; }
    if (formData.latitude == null || formData.longitude == null) {
      toast.error("Cliquez sur « Rechercher l'adresse » avant d'enregistrer");
      return;
    }
    if (!formData.message.trim()) { toast.error('Le message est obligatoire'); return; }

    const payload = {
      name: formData.name.trim(),
      card_id: formData.card_id,
      address: formData.addressLabel || formData.address,
      latitude: Number(formData.latitude),
      longitude: Number(formData.longitude),
      radius: Number(formData.radius),
      message: formData.message.trim(),
      is_active: formData.is_active,
    };

    setSaving(true);
    try {
      if (editingId) {
        await geofencesAPI.update(editingId, payload);
        toast.success('Zone mise à jour');
      } else {
        await geofencesAPI.create(payload);
        toast.success('Zone créée');
      }
      setDialogOpen(false);
      setFormData(EMPTY_FORM);
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (geo) => {
    setTogglingId(geo.id);
    try {
      await geofencesAPI.update(geo.id, { is_active: !geo.is_active });
      toast.success(geo.is_active ? 'Zone désactivée' : 'Zone activée');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de la mise à jour');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await geofencesAPI.delete(deleteId);
      toast.success('Zone supprimée');
      setGeofences((gs) => gs.filter((g) => g.id !== deleteId));
      fetchData();
    } catch {
      toast.error('Échec de la suppression');
    }
    setDeleteId(null);
  };

  const getCardName = (cardId) => cards.find((c) => c.id === cardId)?.name || 'Carte inconnue';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const planLabel = meta.plan_limit == null
    ? 'Plan Pro'
    : meta.plan_limit === 1 ? 'Plan Essentiel : 1 zone' : `Plan Pro : ${meta.plan_limit} zones`;
  const atPlanLimit = meta.plan_limit != null && meta.total_count >= meta.plan_limit;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="geofences-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Notifications de proximité
          </h1>
          <p className="text-slate-600 mt-1 max-w-2xl">
            Vos clients reçoivent une notification quand ils passent près de votre boutique.
          </p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={openCreate}
          disabled={cards.length === 0 || atPlanLimit}
          data-testid="create-geofence-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Ajouter une zone
        </Button>
      </div>

      {/* ── Summary banner ── */}
      <Card className="border-slate-200 bg-slate-50/60">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-slate-900" data-testid="geofences-active-count">
              {meta.active_count} / {meta.apple_limit}
            </span>
            <span className="text-slate-600">zones actives sur Apple Wallet</span>
          </div>
          <span className="hidden sm:inline text-slate-300">•</span>
          <Badge variant="outline" className="bg-white" data-testid="plan-badge">{planLabel}</Badge>
          {meta.apple_limit_reached && (
            <div className="flex items-center gap-2 text-amber-700 sm:ml-auto">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">Limite Apple atteinte. Désactivez une zone pour en activer une autre.</span>
            </div>
          )}
        </CardContent>
      </Card>

      {cards.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
          Créez d'abord une carte de fidélité avant d'ajouter des zones.
        </div>
      )}

      {/* ── List ── */}
      {geofences.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {geofences.map((geo) => (
            <Card
              key={geo.id}
              className={`border-slate-200 transition ${geo.is_active ? 'bg-white' : 'bg-slate-50 opacity-80'}`}
              data-testid={`geofence-${geo.id}`}
            >
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${geo.is_active ? 'bg-green-50' : 'bg-slate-100'}`}>
                    <MapPin className={`w-5 h-5 ${geo.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={geo.is_active}
                      disabled={togglingId === geo.id}
                      onCheckedChange={() => handleToggleActive(geo)}
                      data-testid={`geofence-toggle-${geo.id}`}
                    />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 leading-tight" data-testid={`geofence-name-${geo.id}`}>{geo.name}</h3>
                  {geo.address ? (
                    <p className="text-xs text-slate-500 mt-0.5">{geo.address}</p>
                  ) : (
                    <p className="text-xs text-slate-400 italic mt-0.5">
                      {geo.latitude?.toFixed(4)}, {geo.longitude?.toFixed(4)} (coordonnées brutes)
                    </p>
                  )}
                </div>
                <p className="text-sm text-slate-700 leading-snug line-clamp-2">{geo.message}</p>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                  <Badge variant="outline" className="text-[11px] font-normal">{getCardName(geo.card_id)}</Badge>
                  <span>•</span>
                  <span>Rayon {geo.radius}m</span>
                  <span>•</span>
                  <span className={geo.is_active ? 'text-green-600 font-medium' : 'text-slate-400'}>
                    {geo.is_active ? 'Active' : 'Désactivée'}
                  </span>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline" size="sm" className="flex-1"
                    onClick={() => openEdit(geo)}
                    data-testid={`geofence-edit-${geo.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    onClick={() => setDeleteId(geo.id)}
                    data-testid={`geofence-delete-${geo.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <MapPin className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune zone créée</h3>
            <p className="text-slate-500 text-center mb-6 max-w-sm">
              Cliquez sur « Ajouter une zone » pour commencer.
            </p>
            {cards.length > 0 && !atPlanLimit && (
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" /> Ajouter une zone
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Create/Edit Dialog ── */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditingId(null); setFormData(EMPTY_FORM); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifier la zone' : 'Nouvelle zone'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">

            <div className="space-y-2">
              <Label htmlFor="geo-name">Nom de la zone *</Label>
              <Input
                id="geo-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Boutique principale"
                required
                data-testid="geofence-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="geo-card">Carte associée *</Label>
              <Select
                value={formData.card_id}
                onValueChange={(v) => setFormData({ ...formData, card_id: v })}
              >
                <SelectTrigger id="geo-card" data-testid="geofence-card-select">
                  <SelectValue placeholder="Choisir une carte" />
                </SelectTrigger>
                <SelectContent>
                  {cards.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="geo-address">Adresse *</Label>
              <div className="flex gap-2">
                <Input
                  id="geo-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value, latitude: null, longitude: null, addressScore: null, addressLabel: '' })}
                  placeholder="11 rue de la Paix, 75002 Paris"
                  required
                  data-testid="geofence-address-input"
                />
                <Button
                  type="button" variant="outline"
                  onClick={handleGeocode}
                  disabled={geocoding || !formData.address.trim()}
                  data-testid="geofence-geocode-btn"
                >
                  {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="hidden sm:inline ml-1.5">Rechercher</span>
                </Button>
              </div>
              {formData.addressLabel && formData.addressScore != null && (
                <div className={`text-xs px-3 py-2 rounded-lg border flex items-start gap-2 ${scoreColor(formData.addressScore)}`} data-testid="geocode-result">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{formData.addressLabel}</p>
                    <p className="opacity-80 mt-0.5">{scoreLabel(formData.addressScore)}</p>
                    {formData.addressScore < 0.4 && (
                      <p className="mt-1">Adresse incertaine — vérifiez ou affinez la saisie.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="geo-radius">Rayon</Label>
                <span className="text-sm font-semibold text-slate-900">{formData.radius} m</span>
              </div>
              <Slider
                id="geo-radius"
                min={50} max={1000} step={50}
                value={[formData.radius]}
                onValueChange={([v]) => setFormData({ ...formData, radius: v })}
                data-testid="geofence-radius-slider"
              />
              <div className="flex items-start gap-1.5 text-[11px] text-slate-500">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Apple utilise environ 100m pour déclencher la notification. Ce rayon reste indicatif pour vos propres repères.</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="geo-message">Message affiché *</Label>
              <Textarea
                id="geo-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 50) })}
                placeholder="Bienvenue chez LoyalPass ! Votre carte vous attend."
                maxLength={50}
                rows={2}
                data-testid="geofence-message-input"
              />
              <div className="flex justify-between text-[11px] text-slate-400">
                <span>Apparaît sur l'écran verrouillé du client à proximité</span>
                <span>{formData.message.length}/50</span>
              </div>
            </div>

            <div className="flex items-center justify-between py-2 border-t border-slate-100">
              <div>
                <Label className="font-medium">Zone activée</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Les zones désactivées sont conservées mais retirées des passes.
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                data-testid="geofence-active-toggle"
              />
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={saving || formData.latitude == null}
                data-testid="save-geofence-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingId ? 'Enregistrer' : 'Créer la zone'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette zone ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les clients ne recevront plus de notification à cet endroit.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-geofence-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GeofencesPage;
